// ============================================================================
//  My Spotify recently played (shown to every visitor — no login needed)
// ============================================================================
// The OWNER's Spotify listening history is fetched in the background by a
// scheduled GitHub Actions job (see .github/workflows/spotify-recents-sync.yml)
// and written to the Firestore document `music/recently-played`. This module
// simply reads that document. Visitors never have to connect anything.
//
// Playback of the found tracks goes through Spotify's official embed widget;
// the listed tracks themselves are fetched server-side on the owner's behalf.
import { doc, getDoc } from 'firebase/firestore';
import { firestoreDb, hasFirebaseConfig } from './firebase';

export interface SpotifyRecentlyPlayedItem {
  id: string;
  title: string;
  artist: string;
  album: string;
  /** Best available album artwork URL (may be empty) */
  artwork: string;
  durationSeconds: number;
  spotifyUrl: string;
  /** ISO timestamp of when the owner listened (absent on older documents) */
  playedAt?: string;
}

const RECENTLY_PLAYED_COLLECTION = 'music';
const RECENTLY_PLAYED_DOC = 'recently-played';

/** Whether a backend source (Firestore) is configured at all. */
export function isOwnerRecentlyPlayedAvailable(): boolean {
  return hasFirebaseConfig;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function sanitizeItem(value: unknown): SpotifyRecentlyPlayedItem | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const id = asString(record.id);
  const title = asString(record.title);
  const spotifyUrl = asString(record.spotifyUrl);
  if (!id || !title || !spotifyUrl) return null;

  return {
    id,
    title,
    artist: asString(record.artist) || 'Unknown artist',
    album: asString(record.album),
    artwork: asString(record.artwork),
    durationSeconds: asNumber(record.durationSeconds),
    spotifyUrl,
    playedAt: asString(record.playedAt) || undefined,
  };
}

/** Reads the site owner's recently played tracks from Firestore. */
export async function fetchOwnerRecentlyPlayed(): Promise<SpotifyRecentlyPlayedItem[]> {
  if (!hasFirebaseConfig || !firestoreDb) {
    throw new Error('Firestore is not configured');
  }

  const snapshot = await getDoc(doc(firestoreDb, RECENTLY_PLAYED_COLLECTION, RECENTLY_PLAYED_DOC));
  if (!snapshot.exists()) return [];

  const data = snapshot.data();
  const rawTracks = data?.tracks;
  if (!Array.isArray(rawTracks)) return [];

  return rawTracks
    .map(sanitizeItem)
    .filter((item): item is SpotifyRecentlyPlayedItem => item !== null);
}
